import { UserRole, UserRoles } from '../constants/userRoles'
import { BaseUser, ProfileCompletion, AccountStatus } from '../types/userTypes'

// Validation result interface
export interface ValidationResult {
  isValid: boolean
  errors: string[]
  warnings: string[]
}

// Field requirement levels
export type FieldRequirement = 'required' | 'optional' | 'conditional'

// Base user field requirements (common to all roles)
const BASE_USER_REQUIRED_FIELDS = [
  'firstName',
  'lastName',
  'email',
  'role',
  'accountStatus',
] as const

const BASE_USER_OPTIONAL_FIELDS = [
  'phoneNumber',
  'timezone',
  'displayName',
  'photoURL',
] as const

// Role-specific field requirements
const ROLE_FIELD_REQUIREMENTS: Record<
  UserRole,
  {
    required: string[]
    optional: string[]
    conditional: Record<string, string[]>
  }
> = {
  [UserRoles.SUPER_ADMIN]: {
    required: [...BASE_USER_REQUIRED_FIELDS],
    optional: [...BASE_USER_OPTIONAL_FIELDS],
    conditional: {},
  },

  [UserRoles.CORPORATE]: {
    required: [
      ...BASE_USER_REQUIRED_FIELDS,
      'corporationName',
      'corporationType',
      'billingAddress',
      'billingCycle',
      'planTier',
      'maxTenants',
      'maxProperties',
      'accountMode',
    ],
    optional: [
      ...BASE_USER_OPTIONAL_FIELDS,
      'industry',
      'taxId',
      'paymentMethod',
    ],
    conditional: {
      enterprise_plan: ['taxId', 'industry'],
    },
  },

  [UserRoles.ADMIN]: {
    required: [
      ...BASE_USER_REQUIRED_FIELDS,
      'adminLevel',
      'notificationPreferences',
    ],
    optional: [
      ...BASE_USER_OPTIONAL_FIELDS,
      'assignedCorporateId',
      'jobTitle',
      'department',
      'employeeId',
      'supervisor',
    ],
    conditional: {
      corporate_level: ['assignedCorporateId'],
    },
  },

  [UserRoles.MANAGEMENT]: {
    required: [
      ...BASE_USER_REQUIRED_FIELDS,
      'managerType',
      'managedProperties',
      'workHours',
    ],
    optional: [
      ...BASE_USER_OPTIONAL_FIELDS,
      'licenseNumber',
      'licenseState',
      'licenseExpiration',
      'geographicScope',
    ],
    conditional: {
      property_manager: ['licenseNumber', 'licenseState'],
    },
  },

  [UserRoles.TENANT]: {
    required: [
      ...BASE_USER_REQUIRED_FIELDS,
      'employmentStatus',
      'assignedProperties',
      'propertyRole',
      'complianceStatus',
    ],
    optional: [
      ...BASE_USER_OPTIONAL_FIELDS,
      'dateOfBirth',
      'emergencyContact',
      'occupants',
      'employer',
      'monthlyIncome',
    ],
    conditional: {
      employed: ['employer', 'monthlyIncome'],
    },
  },
}

/**
 * Validates user data based on role requirements
 */
export const validateUserData = (
  userData: Partial<BaseUser>
): ValidationResult => {
  const errors: string[] = []
  const warnings: string[] = []

  if (!userData.role) {
    errors.push('User role is required')
    return { isValid: false, errors, warnings }
  }

  const requirements = ROLE_FIELD_REQUIREMENTS[userData.role]

  // Check required fields
  for (const field of requirements.required) {
    if (!userData[field as keyof BaseUser]) {
      errors.push(`${field} is required for ${userData.role} role`)
    }
  }

  // Check conditional fields
  for (const [condition, fields] of Object.entries(requirements.conditional)) {
    if (shouldApplyCondition(userData, condition)) {
      for (const field of fields) {
        if (!userData[field as keyof BaseUser]) {
          errors.push(`${field} is required when ${condition}`)
        }
      }
    }
  }

  // Validate email format
  if (userData.email && !isValidEmail(userData.email)) {
    errors.push('Invalid email format')
  }

  // Validate phone number format
  if (userData.phoneNumber && !isValidPhoneNumber(userData.phoneNumber)) {
    warnings.push('Phone number format may be invalid')
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  }
}

/**
 * Validates minimal registration data (firstName, lastName, email)
 */
export const validateMinimalRegistration = (data: {
  firstName?: string
  lastName?: string
  email?: string
}): ValidationResult => {
  const errors: string[] = []
  const warnings: string[] = []

  if (!data.firstName?.trim()) {
    errors.push('First name is required')
  }

  if (!data.lastName?.trim()) {
    errors.push('Last name is required')
  }

  if (!data.email?.trim()) {
    errors.push('Email is required')
  } else if (!isValidEmail(data.email)) {
    errors.push('Invalid email format')
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  }
}

/**
 * Calculates profile completion percentage
 */
export const calculateProfileCompletion = (
  userData: Partial<BaseUser>
): ProfileCompletion => {
  if (!userData.role) {
    return {
      basic: false,
      contact: false,
      professional: false,
      roleSpecific: false,
      overall: 0,
    }
  }
  // Basic completion (firstName, lastName, email)
  const basicFields = ['firstName', 'lastName', 'email']
  const basicComplete = basicFields.every(
    field => userData[field as keyof BaseUser]
  )

  // Contact completion (phone only - timezone now property-based)
  const contactFields = ['phoneNumber']
  const contactComplete = contactFields.some(
    field => userData[field as keyof BaseUser]
  )

  // Professional completion (role-specific)
  const professionalFields = getProfessionalFields(userData.role)
  const professionalComplete = professionalFields.every(
    field => userData[field as keyof BaseUser]
  )

  // Role-specific completion
  const roleSpecificFields = getRoleSpecificFields(userData.role)
  const roleSpecificComplete = roleSpecificFields.every(
    field => userData[field as keyof BaseUser]
  )

  // Calculate overall percentage
  const totalSections = 4 // basic, contact, professional, roleSpecific
  let completedSections = 0

  if (basicComplete) completedSections++
  if (contactComplete) completedSections++
  if (professionalComplete) completedSections++
  if (roleSpecificComplete) completedSections++

  const overall = Math.round((completedSections / totalSections) * 100)

  return {
    basic: basicComplete,
    contact: contactComplete,
    professional: professionalComplete,
    roleSpecific: roleSpecificComplete,
    overall,
  }
}

/**
 * Gets the next required fields for profile completion
 */
export const getNextRequiredFields = (
  userData: Partial<BaseUser>
): string[] => {
  if (!userData.role) return ['role']

  const requirements = ROLE_FIELD_REQUIREMENTS[userData.role]
  const missingFields: string[] = []

  for (const field of requirements.required) {
    if (!userData[field as keyof BaseUser]) {
      missingFields.push(field)
    }
  }

  return missingFields
}

/**
 * Validates account status transition
 */
export const validateAccountStatusTransition = (
  currentStatus: AccountStatus,
  newStatus: AccountStatus,
  userRole: UserRole
): ValidationResult => {
  const errors: string[] = []
  const warnings: string[] = []

  // Define valid transitions
  const validTransitions: Record<AccountStatus, AccountStatus[]> = {
    pending: ['active', 'inactive', 'deleted'],
    active: ['inactive', 'suspended', 'deleted'],
    inactive: ['active', 'suspended', 'deleted'],
    suspended: ['active', 'inactive', 'deleted'],
    seed: ['active', 'inactive', 'deleted'], // Seed accounts can be promoted to active or deactivated
    deleted: [], // Deleted accounts cannot transition to any other status (soft delete is permanent)
  }

  if (!validTransitions[currentStatus].includes(newStatus)) {
    errors.push(`Cannot transition from ${currentStatus} to ${newStatus}`)
  }

  // Role-specific restrictions
  if (userRole === UserRoles.SUPER_ADMIN && newStatus === 'suspended') {
    errors.push('Super Admin accounts cannot be suspended')
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  }
}

// Helper functions

function shouldApplyCondition(
  userData: Partial<BaseUser>,
  condition: string
): boolean {
  switch (condition) {
    case 'enterprise_plan':
      return (userData as Record<string, unknown>).planTier === 'enterprise'
    case 'corporate_level':
      return (
        (userData as Record<string, unknown>).adminLevel === 'corporate_level'
      )
    case 'property_manager':
      return (userData as Record<string, unknown>).managerType === 'property'
    case 'employed':
      return (
        (userData as Record<string, unknown>).employmentStatus === 'employed'
      )
    default:
      return false
  }
}

function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

function isValidPhoneNumber(phone: string): boolean {
  // Basic phone validation - can be enhanced
  const phoneRegex = /^[+]?[1-9][\d]{0,15}$/
  return phoneRegex.test(phone.replace(/[\s\-()]/g, ''))
}

function getProfessionalFields(role: UserRole): string[] {
  switch (role) {
    case UserRoles.CORPORATE:
      return ['corporationName', 'corporationType']
    case UserRoles.ADMIN:
      return ['jobTitle', 'department']
    case UserRoles.MANAGEMENT:
      return ['managerType', 'licenseNumber']
    case UserRoles.TENANT:
      return ['employmentStatus']
    default:
      return []
  }
}

function getRoleSpecificFields(role: UserRole): string[] {
  switch (role) {
    case UserRoles.CORPORATE:
      return ['billingAddress', 'planTier', 'maxTenants']
    case UserRoles.ADMIN:
      return ['adminLevel', 'notificationPreferences']
    case UserRoles.MANAGEMENT:
      return ['managedProperties', 'workHours']
    case UserRoles.TENANT:
      return ['assignedProperties', 'propertyRole', 'complianceStatus']
    default:
      return []
  }
}

/**
 * Extracts first and last name from display name or email
 */
export const extractNameFromDisplayNameOrEmail = (
  displayName?: string,
  email?: string
): { firstName: string; lastName: string } => {
  if (displayName?.trim()) {
    const nameParts = displayName.trim().split(/\s+/)
    if (nameParts.length >= 2) {
      return {
        firstName: nameParts[0],
        lastName: nameParts.slice(1).join(' '),
      }
    } else if (nameParts.length === 1) {
      return {
        firstName: nameParts[0],
        lastName: '',
      }
    }
  }

  if (email) {
    // Extract from email local part
    const localPart = email.split('@')[0]
    const parts = localPart.split(/[._-]/)

    if (parts.length >= 2) {
      return {
        firstName: capitalizeFirstLetter(parts[0]),
        lastName: capitalizeFirstLetter(parts[1]),
      }
    } else {
      return {
        firstName: capitalizeFirstLetter(localPart),
        lastName: '',
      }
    }
  }

  return {
    firstName: '',
    lastName: '',
  }
}

function capitalizeFirstLetter(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}
